import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Checkbox,
  Button,
  Stack,
} from "@mui/material";
import api from "../../../services/api";
import { getSizeLabel } from "../../../utils/getSizeLabel";

const CakeSizeFlavorSelector = ({
  selectedSizeIds,
  selectedFlavorIds,
  onChange,
}) => {
  const [sizes, setSizes] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [sizesRes, flavorsRes] = await Promise.all([
          api.get("/sizes"),
          api.get("/flavors"),
        ]);

        const specialtySizes = (sizesRes.data || []).filter(
          (size) => size.category === "specialty"
        );

        const specialtyFlavors = (flavorsRes.data || []).filter(
          (flavor) =>
            Array.isArray(flavor.category) &&
            flavor.category.includes("specialtyCake")
        );

        setSizes(specialtySizes);
        setFlavors(specialtyFlavors);
      } catch (err) {
        console.error("Failed to load size/flavor options:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  const handleSizeChange = (event, newValue) => {
    onChange({ sizes: newValue.map((item) => item._id) });
  };

  const handleFlavorChange = (event, newValue) => {
    onChange({ flavors: newValue.map((item) => item._id) });
  };

  const handleSelectAllSizes = () => {
    onChange({ sizes: sizes.map((s) => s._id) });
  };

  const handleDeselectAllSizes = () => {
    onChange({ sizes: [] });
  };

  const handleSelectAllFlavors = () => {
    onChange({ flavors: flavors.map((f) => f._id) });
  };

  const handleDeselectAllFlavors = () => {
    onChange({ flavors: [] });
  };

  const getSelected = (all, selectedIds) => {
    return all.filter((item) => selectedIds.includes(item._id));
  };

  if (loading) {
    return (
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Sizes */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Select Sizes
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" onClick={handleSelectAllSizes}>
          Select All
        </Button>
        <Button size="small" onClick={handleDeselectAllSizes}>
          Deselect All
        </Button>
      </Stack>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={sizes}
        value={getSelected(sizes, selectedSizeIds)}
        getOptionLabel={(option) => getSizeLabel(option)}
        onChange={handleSizeChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
            {getSizeLabel(option)}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Sizes" />}
        sx={{ mb: 3 }}
      />

      {/* Flavors */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Select Flavors
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" onClick={handleSelectAllFlavors}>
          Select All
        </Button>
        <Button size="small" onClick={handleDeselectAllFlavors}>
          Deselect All
        </Button>
      </Stack>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={flavors}
        value={getSelected(flavors, selectedFlavorIds)}
        getOptionLabel={(option) => option.displayName || option.name}
        onChange={handleFlavorChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
            {option.displayName || option.name}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Flavors" />}
      />
    </Box>
  );
};

export default CakeSizeFlavorSelector;
